import { Link } from "react-router-dom";
import { Blinds, Clock, FileText, HardHat, KeyRound, MapPin, ShieldCheck, Star } from "lucide-react";
import Seo, { buildLocalBusinessSchema } from "@/components/Seo";
import { servicesOptions } from "@/data/services";
import { guides } from "@/data/guides";
import { zones } from "@/data/zones";

const trustItems = [
  {
    id: "urgence-24-7",
    title: "Urgence 24h/24",
    text: "Interventions rapides pour sécuriser vos accès et dépanner vos fermetures.",
  },
  {
    id: "expertise-alu",
    title: "Expertise aluminium",
    text: "Menuiseries, façades et automatismes pensés pour durer.",
  },
  {
    id: "zone-locale",
    title: "Zone Maisons-Alfort",
    text: "Priorité aux demandes locales avec délais d’intervention réduits.",
  },
];

const previewServices = servicesOptions.slice(0, 6);
const previewGuides = guides.slice(0, 3);
const previewZones = [...zones]
  .sort((a, b) => Number(b.priority) - Number(a.priority))
  .slice(0, 9);

const testimonials = [
  {
    id: "maison",
    name: "Claire D.",
    quote: "Intervention ultra rapide sur notre rideau métallique. Équipe professionnelle et rassurante.",
  },
  {
    id: "boutique",
    name: "Boutique PMU",
    quote: "Remplacement de vitre en urgence, devis clair et respecté. Merci Fast Alu !",
  },
  {
    id: "syndic",
    name: "Syndic local",
    quote: "Très bon suivi, artisans soigneux pour la menuiserie alu. Nous recommandons.",
  },
];

const Home = () => {
  const schema = buildLocalBusinessSchema({ city: "Maisons-Alfort" });

  return (
    <div data-testid="home-page">
      <Seo
        title="Fast Alu | Menuiserie Aluminium & Dépannage à Maisons-Alfort"
        description="Intervention rapide à Maisons-Alfort et Val-de-Marne. Menuiserie aluminium, métallerie, automatismes et dépannage 24h/24."
        schema={schema}
      />
      <section className="relative bg-brand-blue text-white" data-testid="home-hero-section">
        <div className="absolute inset-0" aria-hidden="true">
          <img
            src="https://images.unsplash.com/photo-1760025128820-1548e72b3cba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjB3b3JrZXIlMjBpbnN0YWxsaW5nJTIwd2luZG93fGVufDB8fHx8MTc3MjU4MjY3N3ww&ixlib=rb-4.1.0&q=85"
            alt="Artisan en intervention"
            className="w-full h-full object-cover"
            data-testid="hero-bg-image"
          />
          <div className="absolute inset-0 bg-black/70" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 py-24">
          <div className="max-w-2xl space-y-6">
            <span
              className="inline-flex items-center gap-2 bg-brand-red/90 text-white px-4 py-2 rounded-full text-xs uppercase tracking-widest"
              data-testid="hero-badge"
            >
              <MapPin size={14} />
              Maisons-Alfort • Val-de-Marne
            </span>
            <h1 className="font-heading text-5xl uppercase" data-testid="hero-title">
              Dépannage & travaux alu/métal
              <span className="text-brand-red"> – Urgence 24h/24</span>
            </h1>
            <p className="text-lg text-white/80" data-testid="hero-subtitle">
              Serrurerie, rideaux métalliques, métallerie et menuiserie aluminium. Intervention rapide par un artisan
              local à Maisons‑Alfort et dans le Val‑de‑Marne.
            </p>
            <div className="flex flex-wrap gap-3" data-testid="hero-pictos">
              <span
                className="inline-flex items-center gap-2 bg-white/10 px-3 py-2 rounded-full text-xs uppercase"
                data-testid="hero-picto-casque"
              >
                <HardHat size={16} />
                Artisan
              </span>
              <span
                className="inline-flex items-center gap-2 bg-white/10 px-3 py-2 rounded-full text-xs uppercase"
                data-testid="hero-picto-cles"
              >
                <KeyRound size={16} />
                Serrurerie
              </span>
              <span
                className="inline-flex items-center gap-2 bg-white/10 px-3 py-2 rounded-full text-xs uppercase"
                data-testid="hero-picto-rideau"
              >
                <Blinds size={16} />
                Rideaux métalliques
              </span>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="tel:0184160307"
                className="bg-brand-red text-white px-6 py-3 rounded-full uppercase text-xs font-bold text-center"
                data-testid="hero-call-button"
              >
                Appelez maintenant
              </a>
              <Link
                to="/contact"
                className="border border-white text-white px-6 py-3 rounded-full uppercase text-xs font-bold text-center"
                data-testid="hero-quote-button"
              >
                Demander un devis
              </Link>
            </div>
            <div className="flex flex-wrap gap-4 text-sm text-white/80" data-testid="hero-highlights">
              <div className="flex items-center gap-2" data-testid="hero-highlight-rapidite">
                <Clock size={16} />
                Intervention rapide
              </div>
              <div className="flex items-center gap-2" data-testid="hero-highlight-urgence">
                <MapPin size={16} />
                Artisan local
              </div>
              <div className="flex items-center gap-2" data-testid="hero-highlight-devis">
                <FileText size={16} />
                Devis transparent
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-4 mt-6" data-testid="hero-trust-cards">
              <div className="bg-white/10 border border-white/20 rounded-sm p-4" data-testid="hero-trust-card-avis">
                <Star size={18} />
                <p className="text-sm uppercase mt-2">Avis clients</p>
                <p className="text-lg font-semibold">4,9/5</p>
              </div>
              <div className="bg-white/10 border border-white/20 rounded-sm p-4" data-testid="hero-trust-card-securite">
                <ShieldCheck size={18} />
                <p className="text-sm uppercase mt-2">Sécurité certifiée</p>
                <p className="text-lg font-semibold">Interventions 24h/24</p>
              </div>
              <div className="bg-white/10 border border-white/20 rounded-sm p-4" data-testid="hero-trust-card-local">
                <MapPin size={18} />
                <p className="text-sm uppercase mt-2">Zone locale</p>
                <p className="text-lg font-semibold">Val-de-Marne</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-brand-red text-white" data-testid="home-news-banner">
        <div className="max-w-6xl mx-auto px-6 py-3 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-xs uppercase tracking-wider" data-testid="home-news-banner-text">
            Nouveautés & promotions Fast Alu : offres en cours sur rideaux métalliques, vitrages et sécurité.
          </p>
          <Link
            to="/actualites"
            className="bg-white text-brand-red px-4 py-2 rounded-full text-xs uppercase font-bold"
            data-testid="home-news-banner-cta"
          >
            Voir les promos
          </Link>
        </div>
      </section>


      <section className="max-w-6xl mx-auto px-6 py-16" data-testid="home-trust-section">
        <div className="grid md:grid-cols-3 gap-6">
          {trustItems.map((item) => (
            <div
              key={item.id}
              className="bg-white border border-brand-blue/15 p-6 rounded-sm"
              data-testid={`home-trust-card-${item.id}`}
            >
              <p className="font-heading text-xl uppercase" data-testid={`home-trust-title-${item.id}`}>
                {item.title}
              </p>
              <p className="mt-2 text-sm text-brand-ink/70" data-testid={`home-trust-text-${item.id}`}>
                {item.text}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-brand-blue/5" data-testid="home-testimonials">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <h2 className="font-heading text-3xl uppercase" data-testid="home-testimonials-title">
                Avis & retours clients
              </h2>
              <p className="text-brand-ink/70" data-testid="home-testimonials-text">
                Des interventions rapides et une transparence totale sur nos devis.
              </p>
            </div>
            <div className="flex items-center gap-2 text-brand-red" data-testid="home-testimonials-rating">
              <Star size={18} />
              <span className="text-sm uppercase">4,9/5</span>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-6">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-white border border-brand-blue/15 p-6 rounded-sm"
                data-testid={`home-testimonial-${testimonial.id}`}
              >
                <p className="text-sm text-brand-ink/70" data-testid={`home-testimonial-quote-${testimonial.id}`}>
                  “{testimonial.quote}”
                </p>
                <p className="text-xs uppercase font-semibold mt-4" data-testid={`home-testimonial-name-${testimonial.id}`}>
                  {testimonial.name}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-blue/5" data-testid="home-services-preview">
        <div className="max-w-6xl mx-auto px-6 py-16 space-y-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-widest text-brand-red" data-testid="services-kicker">
                Nos services
              </p>
              <h2 className="font-heading text-3xl uppercase" data-testid="services-title">
                Sécurisation & projets aluminium
              </h2>
            </div>
            <Link
              to="/services"
              className="text-sm uppercase font-semibold text-brand-blue"
              data-testid="services-link"
            >
              Voir tous les services
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {previewServices.map((service) => (
              <div
                key={service.slug}
                className="bg-white border border-brand-blue/15 p-6 rounded-sm"
                data-testid={`service-card-${service.slug}`}
              >
                <Link to={`/services/${service.slug}`} data-testid={`home-service-image-link-${service.slug}`}>
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-36 object-cover rounded-sm"
                    data-testid={`home-service-image-${service.slug}`}
                  />
                </Link>
                <h3 className="font-heading text-xl uppercase mt-4" data-testid={`service-title-${service.slug}`}>
                  {service.title}
                </h3>
                <p className="mt-3 text-sm text-brand-ink/70" data-testid={`service-benefits-${service.slug}`}>
                  {service.benefits.join(" • ")}
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <Link
                    to={`/services/${service.slug}`}
                    className="text-xs uppercase font-semibold text-brand-blue"
                    data-testid={`service-more-${service.slug}`}
                  >
                    En savoir plus
                  </Link>
                  <Link
                    to="/contact"
                    className="text-xs uppercase font-semibold text-brand-red"
                    data-testid={`service-cta-${service.slug}`}
                  >
                    Demander un devis
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-16" data-testid="home-zones-preview">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-brand-red" data-testid="zones-kicker">
              Zones d’intervention
            </p>
            <h2 className="font-heading text-3xl uppercase" data-testid="zones-title">
              Maisons-Alfort en priorité
            </h2>
          </div>
          <Link to="/zones" className="text-sm uppercase font-semibold text-brand-blue" data-testid="zones-link">
            Toutes les zones
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-4 mt-6">
          {previewZones.map((zone) => (
            <Link
              key={zone.slug}
              to={`/${zone.slug}`}
              className={`border p-4 rounded-sm transition hover:border-brand-red ${
                zone.priority ? "border-brand-red bg-brand-red/10" : "border-brand-blue/15"
              }`}
              data-testid={`zone-card-${zone.slug}`}
            >
              <p className="font-semibold" data-testid={`zone-name-${zone.slug}`}>
                {zone.name}
              </p>
              <p className="text-sm text-brand-ink/70" data-testid={`zone-desc-${zone.slug}`}>
                {zone.description}
              </p>
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-16" data-testid="home-guides">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h2 className="font-heading text-3xl uppercase" data-testid="home-guides-title">
              Guides & conseils
            </h2>
            <p className="text-brand-ink/70" data-testid="home-guides-text">
              Ressources utiles pour vos projets aluminium, vitrage et rénovation.
            </p>
          </div>
          <Link
            to="/guides"
            className="text-sm uppercase font-semibold text-brand-red"
            data-testid="home-guides-link"
          >
            Voir tous les guides
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-6">
          {previewGuides.map((guide) => (
            <div
              key={guide.slug}
              className="border border-brand-blue/15 p-6 rounded-sm bg-white"
              data-testid={`home-guide-${guide.slug}`}
            >
              <Link to={`/guides/${guide.slug}`} data-testid={`home-guide-image-link-${guide.slug}`}>
                <img
                  src={guide.image}
                  alt={guide.title}
                  className="w-full h-32 object-cover rounded-sm"
                  data-testid={`home-guide-image-${guide.slug}`}
                />
              </Link>
              <h3 className="font-heading text-xl uppercase mt-4" data-testid={`home-guide-title-${guide.slug}`}>
                {guide.title}
              </h3>
              <p className="text-sm text-brand-ink/70 mt-2" data-testid={`home-guide-excerpt-${guide.slug}`}>
                {guide.excerpt}
              </p>
              <Link
                to={`/guides/${guide.slug}`}
                className="inline-flex mt-4 text-xs uppercase font-semibold text-brand-blue"
                data-testid={`home-guide-link-${guide.slug}`}
              >
                Lire le guide
              </Link>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-brand-blue" data-testid="home-promos">
        <div className="max-w-6xl mx-auto px-6 py-12 text-white flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-heading text-3xl uppercase" data-testid="home-promos-title">
              Promotions & actualités
            </h2>
            <p className="text-white/80" data-testid="home-promos-text">
              Consultez les offres en cours et les nouveautés Fast Alu.
            </p>
          </div>
          <Link
            to="/actualites"
            className="bg-white text-brand-blue px-6 py-3 rounded-full uppercase text-xs font-bold"
            data-testid="home-promos-cta"
          >
            Voir les offres
          </Link>
        </div>
      </section>

      <section className="bg-brand-blue text-white" data-testid="home-cta-section">
        <div className="max-w-6xl mx-auto px-6 py-16 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-heading text-3xl uppercase" data-testid="home-cta-title">
              Besoin d’une intervention rapide ?
            </h2>
            <p className="text-sm text-white/80" data-testid="home-cta-text">
              Nos équipes interviennent à Maisons-Alfort et dans tout le Val-de-Marne.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href="tel:0184160307"
              className="bg-white text-brand-blue px-6 py-3 rounded-sm uppercase text-xs font-bold text-center"
              data-testid="home-cta-call"
            >
              Appelez
            </a>
            <Link
              to="/contact"
              className="bg-brand-red text-white px-6 py-3 rounded-sm uppercase text-xs font-bold text-center"
              data-testid="home-cta-quote"
            >
              Demander un devis
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
